/** Pure onboarding rules. No storage, no network. */

export const ONBOARDING_STORAGE_KEY = 'mns-onboard-v1';

export const NEED_IDS = ['driving', 'bank', 'credit', 'health', 'america'] as const;
export type NeedId = (typeof NEED_IDS)[number];

export const REFERRAL_IDS = ['facebook', 'viber', 'telegram', 'friend', 'temple', 'youtube', 'other'] as const;
export type ReferralId = (typeof REFERRAL_IDS)[number];

export type DrivingGoal = 'permit' | 'license';
export const DAILY_MINUTES = [2, 5, 10] as const;
export type DailyMinutes = (typeof DAILY_MINUTES)[number];
export const TIME_OF_DAY = ['morning', 'afternoon', 'evening'] as const;
export type TimeOfDay = (typeof TIME_OF_DAY)[number];
export type NotificationChoice = 'granted' | 'denied' | 'not_now';
export type InviteChoice = 'shared' | 'skipped';
export type TrialChoice = 'started' | 'skipped' | 'invite';
export type EnglishLevel = 1 | 2 | 3 | 4 | 5;
export type Screen =
  | 'welcome'
  | 'intro'
  | 'need'
  | 'driving'
  | 'english'
  | 'react'
  | 'goal'
  | 'goalset'
  | 'referral'
  | 'notifyask'
  | 'notify'
  | 'invite'
  | 'time'
  | 'achieve'
  | 'trial'
  | 'ready';

/** Screens that ask for an answer. Intro, reactions, and prompts are not questions. */
export const QUESTION_SCREENS: readonly Screen[] = ['need', 'driving', 'english', 'goal', 'referral', 'time'];

export const ENGLISH_LEVELS: { level: EnglishLevel; my: string; en: string }[] = [
  { level: 1, my: 'အင်္ဂလိပ်မသိသလောက်ပါ', en: 'I know almost no English' },
  { level: 2, my: 'စကားလုံးအနည်းငယ်', en: 'A few words' },
  { level: 3, my: 'ရိုးရိုးဝါကျ', en: 'Simple sentences' },
  { level: 4, my: 'နေ့စဉ်အကြောင်း ပြောနိုင်ပါတယ်', en: 'I can talk about daily life' },
  { level: 5, my: 'အင်္ဂလိပ်မှာ အဆင်ပြေပါတယ်', en: 'I am comfortable in English' },
];

export const GOAL_LABELS: Record<DailyMinutes, { my: string; en: string }> = {
  2: { my: 'ပေါ့ပေါ့', en: 'Casual' },
  5: { my: 'ပုံမှန်', en: 'Regular' },
  10: { my: 'တကယ်လုပ်', en: 'Serious' },
};

/** Takeaway lines in the five open California lessons, in lesson order. */
export const DRIVING_RULE_COUNTS = [2, 1, 3, 3, 2] as const;

/** Ten-minute block used for the monthly pace. Stated in the helper so it is not a hidden forecast. */
export const LESSON_MINUTE_BLOCK = 10;
/** Sum of the five California lesson durations in the seed: 12+8+10+12+8. */
export const DRIVING_LESSON_MINUTES = 50;
export const DRIVING_LESSON_COUNT = 5;
export const PRO_PASS_DAYS = 7;
export const PRO_REMINDER_LEAD_DAYS = 2;

export const PATH_LESSON_COUNTS: Record<NeedId, number> = {
  driving: DRIVING_LESSON_COUNT,
  health: 4,
  america: 3,
  bank: 1,
  credit: 1,
};

export interface NeedChoice {
  id: NeedId;
  slug: string;
  en: string;
  my: string;
  /** Catalog lesson to open. Null means the path has no lesson yet. */
  lessonSlug: string | null;
  pathEn: string;
  pathMy: string;
}

export const NEED_CHOICES: NeedChoice[] = [
  {
    id: 'driving',
    slug: 'ca-dmv',
    en: 'Driving license',
    my: 'ယာဉ်မောင်းလိုင်စင်',
    lessonSlug: 'right-of-way',
    pathEn: 'California Driving Test',
    pathMy: 'ကယ်လီဖိုးနီးယား ယာဉ်မောင်းစာမေးပွဲ',
  },
  {
    id: 'bank',
    slug: 'renting-id-setup',
    en: 'Bank account',
    my: 'ဘဏ်အကောင့်',
    lessonSlug: null,
    pathEn: 'Renting, Banking & ID',
    pathMy: 'ငှားရမ်းခြင်း၊ ဘဏ်၊ နှင့် ID',
  },
  {
    id: 'credit',
    slug: 'build-your-credit-foundation',
    en: 'Credit',
    my: 'ခရက်ဒစ်',
    lessonSlug: null,
    pathEn: 'Build Your Credit Foundation',
    pathMy: 'ခရက်ဒစ် အခြေခံတည်ဆောက်ခြင်း',
  },
  {
    id: 'health',
    slug: 'health-insurance',
    en: 'Health insurance',
    my: 'ကျန်းမာရေးအာမခံ',
    lessonSlug: 'insurance-words',
    pathEn: 'Health Insurance Basics',
    pathMy: 'ကျန်းမာရေးအာမခံ အခြေခံ',
  },
  {
    id: 'america',
    slug: 'new-to-america',
    en: 'New to America basics',
    my: 'အမေရိက အခြေခံ',
    lessonSlug: 'quiz-1',
    pathEn: 'New to America',
    pathMy: 'အမေရိကကို အသစ်ရောက်သူများအတွက် အခြေခံလမ်းညွှန်',
  },
];

export const REFERRAL_CHOICES: { id: ReferralId; en: string; my: string }[] = [
  { id: 'facebook', en: 'Facebook', my: 'ဖေ့စ်ဘွတ်' },
  { id: 'viber', en: 'Viber', my: 'ဗိုင်ဘာ' },
  { id: 'telegram', en: 'Telegram', my: 'တယ်လီဂရမ်' },
  { id: 'friend', en: 'Friend/family', my: 'မိတ်ဆွေ / မိသားစု' },
  { id: 'temple', en: 'Temple/church', my: 'ဘုရားကျောင်း / အသင်းတော်' },
  { id: 'youtube', en: 'YouTube', my: 'ယူကျု့' },
  { id: 'other', en: 'Other', my: 'အခြား' },
];

export interface OnboardingRecord {
  version: 1;
  guestKey: string;
  need: NeedId | null;
  journeySlug: string | null;
  drivingGoal: DrivingGoal | null;
  drivingState: 'CA' | null;
  testDate: string | null;
  referral: ReferralId | null;
  englishLevel: EnglishLevel | null;
  dailyMinutes: DailyMinutes | null;
  notificationChoice: NotificationChoice | null;
  inviteChoice: InviteChoice | null;
  inviteCode: string | null;
  incomingInvite: string | null;
  timeOfDay: TimeOfDay | null;
  trialChoice: TrialChoice | null;
  proUntil: string | null;
  proReminderOn: string | null;
  proReminderSeen: boolean;
  lessonFinishedAt: string | null;
  claimedAt: string | null;
  profileName: string | null;
  profileEmail: string | null;
  planChoice: 'free' | 'pro' | null;
  lessonPoints: number | null;
}

export interface GoalPace {
  lessonsPerMonth: number;
  drivingDays: number | null;
  lessonCount: number;
}

export interface ReferralEvent {
  guest_key: string;
  need: NeedId;
  journey_slug: string;
  referral_source: ReferralId;
  driving_goal: DrivingGoal | null;
  driving_state: 'CA' | null;
  test_date: string | null;
}

export function needById(id: NeedId): NeedChoice {
  const found = NEED_CHOICES.find((choice) => choice.id === id);
  if (!found) throw new Error(`Unknown need ${id}`);
  return found;
}

export function emptyRecord(guestKey: string): OnboardingRecord {
  return {
    version: 1,
    guestKey,
    need: null,
    journeySlug: null,
    drivingGoal: null,
    drivingState: null,
    testDate: null,
    referral: null,
    englishLevel: null,
    dailyMinutes: null,
    notificationChoice: null,
    inviteChoice: null,
    inviteCode: null,
    incomingInvite: null,
    timeOfDay: null,
    trialChoice: null,
    proUntil: null,
    proReminderOn: null,
    proReminderSeen: false,
    lessonFinishedAt: null,
    claimedAt: null,
    profileName: null,
    profileEmail: null,
    planChoice: null,
    lessonPoints: null,
  };
}

export function localDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function addLocalDays(date: Date, days: number): string {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return localDateKey(next);
}

export function isDailyMinutes(value: number): value is DailyMinutes {
  return value === 2 || value === 5 || value === 10;
}

export function isEnglishLevel(value: number): value is EnglishLevel {
  return value === 1 || value === 2 || value === 3 || value === 4 || value === 5;
}

/** Lessons this pace can finish in 7 days, capped by the lessons that exist. */
export function lessonsInFirstWeek(need: NeedId, minutes: DailyMinutes): number {
  const paced = Math.max(1, Math.round((minutes * 7) / LESSON_MINUTE_BLOCK));
  return Math.min(PATH_LESSON_COUNTS[need], paced);
}

/** Driving uses the real takeaway counts. Other paths count lessons, not invented rules. */
export function firstWeekResult(need: NeedId, minutes: DailyMinutes): { count: number; kind: 'rules' | 'lessons' } {
  const lessons = lessonsInFirstWeek(need, minutes);
  if (need !== 'driving') return { count: lessons, kind: 'lessons' };
  const count = DRIVING_RULE_COUNTS.slice(0, lessons).reduce((sum, rules) => sum + rules, 0);
  return { count, kind: 'rules' };
}

export function questionCount(record: OnboardingRecord): number {
  return screenOrder(record).filter((screen) => QUESTION_SCREENS.includes(screen)).length;
}

/** ca-dmv is English-first. Every other onboarding path is Burmese-first. */
export function guideLanguage(record: OnboardingRecord): 'my' | 'en' {
  return record.need === 'driving' ? 'en' : 'my';
}

/**
 * How prominent Burmese is under a DMV lesson.
 * 1–2: Burmese is the main line. 3: English stays main and Burmese is larger.
 * 4–5, or no answer yet: English main and a quiet Burmese line.
 */
export function burmeseProminence(englishLevel: EnglishLevel | null): 'main' | 'equal' | 'quiet' {
  if (englishLevel == null || englishLevel >= 4) return 'quiet';
  if (englishLevel === 3) return 'equal';
  return 'main';
}

export function guideLine(screen: Screen, record: OnboardingRecord): { my: string; en: string } {
  if (screen === 'intro') {
    const count = questionCount(record);
    return {
      my: `ပထမသင်ခန်းစာမတိုင်ခင် မေးခွန်း ${count} ခုပဲ။`,
      en: `Just ${count} quick questions before your first lesson`,
    };
  }
  if (screen === 'react') {
    if (record.need === 'driving') {
      return { my: 'အိုကေ၊ သိထားတာပေါ်မှာ ဆက်တည်ဆောက်မယ်။', en: "Okay, we'll build on what you know!" };
    }
    return { my: 'အိုကေ၊ အဲဒီကနေ စမယ်။', en: "Okay, we'll start there." };
  }
  if (screen === 'goalset' && record.need && record.dailyMinutes) {
    const result = firstWeekResult(record.need, record.dailyMinutes);
    if (result.kind === 'rules') {
      return {
        my: `ပထမအပတ်မှာ လမ်းစည်းကမ်း ${result.count} ခုလောက်။`,
        en: `That's about ${result.count} road rules in your first week.`,
      };
    }
    return {
      my: `ပထမအပတ်မှာ သင်ခန်းစာ ${result.count} ခုလောက်။`,
      en: `That's about ${result.count} lessons in your first week.`,
    };
  }
  if (screen === 'notifyask') {
    return { my: 'လေ့ကျင့်ဖို့ သတိပေးမယ်။', en: "I'll remind you to practice" };
  }
  if (screen === 'ready') {
    return { my: 'ဒါက ပထမ ၂ မိနစ် သင်ခန်းစာပါ။', en: "Here's your first 2-minute lesson" };
  }
  const lines: Record<Screen, { my: string; en: string }> = {
    welcome: { my: 'ပထမသင်ခန်းစာကို အခမဲ့ စလို့ရပါတယ်။', en: 'You can start the first lesson for free.' },
    intro: { my: '', en: '' },
    need: { my: 'ဘာကို အရင် လိုအပ်ပါသလဲ။', en: 'What do you need first?' },
    driving: { my: 'ခွင့်လက်မှတ်လား၊ လိုင်စင်လား။', en: 'Permit or license?' },
    english: { my: 'အင်္ဂလိပ် ဘယ်လောက် သိပါသလဲ။', en: 'How much English do you know?' },
    react: { my: '', en: '' },
    goal: { my: 'တစ်ရက် နည်းနည်းလေး ဆိုရင် လုံလောက်ပါတယ်။', en: 'A small daily goal is enough.' },
    goalset: { my: '', en: '' },
    referral: { my: 'ကျွန်ုပ်တို့ကို ဘယ်လို ကြားသိပါသလဲ။', en: 'How did you hear about us?' },
    notifyask: { my: '', en: '' },
    notify: { my: 'သတိပေးချက် ဖွင့်နိုင်ပါတယ်။ မဖွင့်လည်း ရပါတယ်။', en: 'You can allow reminders, or skip them.' },
    invite: { my: 'သူငယ်ချင်းလည်း အတူ လေ့လာနိုင်ပါတယ်။', en: 'A friend can study with you.' },
    time: { my: 'ဘယ်အချိန် သတိပေးရမလဲ။', en: 'When should I remind you?' },
    achieve: { my: 'ဒီလမ်းကြောင်းက ဒီအရာတွေကို လေ့ကျင့်ခိုင်းပါတယ်။', en: 'Here is what this path can help you practice.' },
    trial: { my: 'အခမဲ့သင်ခန်းစာတွေက ဒါမပါဘဲ သုံးလို့ရပါတယ်။', en: 'Free lessons stay available.' },
    ready: { my: '', en: '' },
  };
  return lines[screen];
}

/** Pace from a 10-minute lesson. Driving days finish the 50 minutes of lessons, not a DMV score. */
export function goalPace(need: NeedId, minutes: DailyMinutes): GoalPace {
  return {
    lessonsPerMonth: Math.round((minutes * 30) / LESSON_MINUTE_BLOCK),
    drivingDays: need === 'driving' ? Math.ceil(DRIVING_LESSON_MINUTES / minutes) : null,
    lessonCount: PATH_LESSON_COUNTS[need],
  };
}

export function inviteCodeFor(guestKey: string): string {
  const compact = guestKey.replace(/[^a-zA-Z0-9]/g, '');
  if (compact.length >= 8) return compact.slice(0, 12);
  return `${compact}mnsinvite`.slice(0, 12);
}

export function inviteLink(origin: string, code: string): string {
  const base = origin.replace(/\/$/, '');
  return `${base}/start?invite=${encodeURIComponent(code)}`;
}

export function inviteShareText(link: string): string {
  return `My New Start မှာ အတူတူ လေ့လာကြမယ်။ ဒီလင့်က Pro ၇ ရက် အခမဲ့ပါ။ အခမဲ့သင်ခန်းစာတွေက ဒီမပါဘဲလည်း သုံးလို့ရပါတယ်။ ${link}`;
}

export function isOwnInvite(record: OnboardingRecord): boolean {
  if (!record.incomingInvite) return false;
  return record.incomingInvite === record.inviteCode || record.incomingInvite === inviteCodeFor(record.guestKey);
}

/** Premium paths, or a friend link that is not this person's own code. */
export function trialRelevant(record: OnboardingRecord): boolean {
  if (record.proUntil) return true;
  if (record.incomingInvite && !isOwnInvite(record)) return true;
  return record.need === 'bank' || record.need === 'credit';
}

/** Start the 7-day clock once. Does not mark the trial screen answered. */
export function startProClock(record: OnboardingRecord, now = new Date()): OnboardingRecord {
  if (record.proUntil) return record;
  const until = new Date(now.getTime() + PRO_PASS_DAYS * 24 * 60 * 60 * 1000);
  return {
    ...record,
    proUntil: until.toISOString(),
    proReminderOn: addLocalDays(now, PRO_PASS_DAYS - PRO_REMINDER_LEAD_DAYS),
    proReminderSeen: false,
  };
}

export function applyProPass(record: OnboardingRecord, choice: 'started' | 'invite', now = new Date()): OnboardingRecord {
  return { ...startProClock(record, now), trialChoice: choice };
}

export function reminderDue(record: OnboardingRecord | null, now = new Date()): boolean {
  if (!record?.proUntil || !record.proReminderOn || record.proReminderSeen) return false;
  if (new Date(record.proUntil).getTime() <= now.getTime()) return false;
  return localDateKey(now) >= record.proReminderOn;
}

export function guestPassActive(record: OnboardingRecord | null, now = new Date()): boolean {
  if (!record?.proUntil) return false;
  return new Date(record.proUntil).getTime() > now.getTime();
}

export function onboardingComplete(record: OnboardingRecord | null): boolean {
  if (!record?.need || !record.referral || !record.dailyMinutes || !record.timeOfDay) return false;
  if (!record.notificationChoice || !record.inviteChoice) return false;
  if (record.need === 'driving' && !record.drivingGoal) return false;
  if (record.need === 'driving' && !isEnglishLevel(record.englishLevel || 0)) return false;
  return true;
}

export function screenOrder(record: OnboardingRecord): Screen[] {
  const screens: Screen[] = ['welcome', 'intro', 'need'];
  if (record.need === 'driving') screens.push('driving', 'english');
  screens.push('react', 'goal', 'goalset', 'referral', 'notifyask', 'notify', 'invite', 'time', 'achieve', 'ready');
  return screens;
}

export function withNeed(record: OnboardingRecord, need: NeedId): OnboardingRecord {
  const choice = needById(need);
  const driving = need === 'driving';
  return {
    ...record,
    need,
    journeySlug: choice.slug,
    drivingGoal: driving ? record.drivingGoal : null,
    drivingState: driving ? 'CA' : null,
    testDate: driving ? record.testDate : null,
    englishLevel: driving ? record.englishLevel : null,
  };
}

/** Empty is allowed. A real calendar day is kept. Anything else is dropped. */
export function normalizeTestDate(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  const parsed = new Date(`${trimmed}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.toISOString().slice(0, 10) !== trimmed) return null;
  return trimmed;
}

export function canContinue(screen: Screen, record: OnboardingRecord): boolean {
  if (screen === 'intro' || screen === 'react' || screen === 'goalset' || screen === 'achieve') return true;
  if (screen === 'need') return record.need != null;
  if (screen === 'driving') return record.drivingGoal === 'permit' || record.drivingGoal === 'license';
  if (screen === 'english') return isEnglishLevel(record.englishLevel || 0);
  if (screen === 'goal') return record.dailyMinutes === 2 || record.dailyMinutes === 5 || record.dailyMinutes === 10;
  if (screen === 'referral') return record.referral != null;
  if (screen === 'time') return record.timeOfDay === 'morning' || record.timeOfDay === 'afternoon' || record.timeOfDay === 'evening';
  return false;
}

export function nextScreen(screen: Screen, record: OnboardingRecord): Screen {
  const order = screenOrder(record);
  const index = order.indexOf(screen);
  if (index < 0 || index >= order.length - 1) return 'ready';
  return order[index + 1];
}

export function previousScreen(screen: Screen, record: OnboardingRecord): Screen | null {
  const order = screenOrder(record);
  const index = order.indexOf(screen);
  if (index <= 0) return null;
  return order[index - 1];
}

/** 0 on welcome, filled on the ready screen. */
export function progressFraction(screen: Screen, record: OnboardingRecord): number {
  if (screen === 'welcome') return 0;
  if (screen === 'ready') return 1;
  const order = screenOrder(record);
  const index = order.indexOf(screen);
  if (index <= 0) return 0;
  return index / (order.length - 1);
}

export function resumeScreen(record: OnboardingRecord): Screen {
  if (!record.need) return 'welcome';
  if (record.need === 'driving' && !record.drivingGoal) return 'driving';
  if (record.need === 'driving' && !isEnglishLevel(record.englishLevel || 0)) return 'english';
  if (!record.dailyMinutes) return 'goal';
  if (!record.referral) return 'referral';
  if (!record.notificationChoice) return 'notifyask';
  if (!record.inviteChoice) return 'invite';
  if (!record.timeOfDay) return 'time';
  return 'ready';
}

export function referralEvent(record: OnboardingRecord): ReferralEvent | null {
  if (!record.guestKey || !record.need || !record.journeySlug || !record.referral) return null;
  if (record.need === 'driving' && record.drivingGoal == null) return null;
  if (record.journeySlug !== needById(record.need).slug) return null;
  return {
    guest_key: record.guestKey,
    need: record.need,
    journey_slug: record.journeySlug,
    referral_source: record.referral,
    driving_goal: record.need === 'driving' ? record.drivingGoal : null,
    driving_state: record.need === 'driving' ? 'CA' : null,
    test_date: record.need === 'driving' ? record.testDate : null,
  };
}

export function firstLessonPath(need: NeedId): string {
  const choice = needById(need);
  if (!choice.lessonSlug) return '/start/lesson';
  return `/courses/${choice.slug}/topics/${choice.lessonSlug}`;
}

export function homeAfterAccount(need: NeedId): string {
  const choice = needById(need);
  return choice.lessonSlug ? `/courses/${choice.slug}` : '/dashboard';
}

export function guestPathAllowed(record: OnboardingRecord | null, slug: string): boolean {
  if (!record?.need || !record.journeySlug || !onboardingComplete(record)) return false;
  return record.journeySlug === slug;
}

export function guestLessonAllowed(record: OnboardingRecord | null, slug: string, lessonSlug: string): boolean {
  if (!record?.need || !record.journeySlug || !onboardingComplete(record)) return false;
  const choice = needById(record.need);
  return Boolean(choice.lessonSlug) && choice.slug === slug && choice.lessonSlug === lessonSlug && record.journeySlug === slug;
}

export function guestPracticeAllowed(record: OnboardingRecord | null, slug: string, mode: string | undefined): boolean {
  if (mode !== 'topic' && mode !== 'missed') return false;
  if (!record?.need || !record.journeySlug || !onboardingComplete(record)) return false;
  const choice = needById(record.need);
  return Boolean(choice.lessonSlug) && choice.slug === slug && record.journeySlug === slug;
}

/** Bank and credit have no catalog lesson. The short checklist still counts as the first lesson. */
export function guestStarterAllowed(record: OnboardingRecord | null, slug: string): boolean {
  if (!record?.need || !record.journeySlug || !onboardingComplete(record)) return false;
  const choice = needById(record.need);
  return choice.lessonSlug == null && choice.slug === slug && record.journeySlug === slug;
}
